import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close mobile menu and dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { label: 'Forside', href: '/' },
    {
      label: 'Ydelser',
      href: '/ydelser/tig-svejsning',
      dropdown: [
        { label: 'TIG-svejsning', href: '/ydelser/tig-svejsning' },
        { label: 'CO2 / MIG-MAG', href: '/ydelser/co2-mig-mag' },
        { label: 'Industriservice', href: '/ydelser/industriservice' },
        { label: 'Svejsning i aluminium', href: '/ydelser/svejsning-i-aluminium' },
      ],
    },
    { label: 'Referencer', href: '/referencer' },
    { label: 'Kontakt', href: '/kontakt' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <span className="text-lg sm:text-xl font-extrabold tracking-wider text-slate-950 uppercase">
            JC Svejseteknik
          </span>
        </Link>

        {/* Desktop Navigation (Visible above 1200px / xl) */}
        <nav className="hidden xl:flex items-center gap-8">
          {navItems.map((item) => {
            if (item.dropdown) {
              return (
                <div key={item.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    className={`flex items-center gap-1 text-sm font-medium uppercase tracking-wider transition-colors hover:text-slate-950 ${
                      location.pathname.startsWith('/ydelser')
                        ? 'text-slate-950 font-semibold'
                        : 'text-slate-600'
                    }`}
                  >
                    {item.label}
                    <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div
                      onMouseLeave={() => setIsDropdownOpen(false)}
                      className="absolute left-0 mt-2 w-56 rounded-md border border-slate-200 bg-brand-gray p-2 shadow-xl"
                    >
                      {item.dropdown.map((subItem) => (
                        <NavLink
                          key={subItem.label}
                          to={subItem.href}
                          className={({ isActive }) =>
                            `block rounded px-4 py-2 text-sm transition-colors hover:bg-slate-100 hover:text-slate-950 ${
                              isActive ? 'text-brand-accent bg-slate-50 font-medium' : 'text-slate-500'
                            }`
                          }
                        >
                          {subItem.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.label}
                to={item.href}
                className={({ isActive }) =>
                  `text-sm font-medium uppercase tracking-wider transition-colors hover:text-slate-950 ${
                    isActive ? 'text-slate-950 font-semibold' : 'text-slate-600'
                  }`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Contact CTA (Desktop xl) */}
        <div className="hidden xl:flex items-center gap-6">
          <a
            href="tel:+4561167778"
            className="flex items-center gap-2 text-sm font-semibold text-slate-800 transition-colors hover:text-slate-950 whitespace-nowrap"
          >
            <i className="fa-solid fa-phone text-brand-accent" />
            <span className="whitespace-nowrap">+45 61 16 77 78</span>
          </a>
          <Link
            to="/kontakt"
            className="rounded-md bg-brand-accent px-5 py-2.5 text-sm font-semibold tracking-wider text-white transition-all hover:bg-brand-accent/80 hover:shadow-lg hover:shadow-brand-accent/20"
          >
            Få et tilbud
          </Link>
        </div>

        {/* Mobile Hamburger Button (Visible below 1200px / xl) */}
        <div className="flex items-center gap-4 xl:hidden">
          <a
            href="tel:+4561167778"
            className="flex h-10 w-10 items-center justify-center text-slate-700 transition-colors hover:text-brand-accent"
            aria-label="Ring til os"
          >
            <i className="fa-solid fa-phone text-lg" />
          </a>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-10 w-10 items-center justify-center text-slate-600 hover:text-slate-900 focus:outline-none"
            aria-label="Åben menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu (Visible below 1200px / xl) */}
      {isOpen && (
        <div className="border-t border-slate-200 bg-brand-dark px-4 py-4 shadow-2xl xl:hidden">
          <div className="space-y-3">
            {navItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                      className="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-base font-medium text-slate-600 transition-colors hover:bg-slate-100"
                    >
                      <span className="uppercase tracking-wider text-sm font-semibold">{item.label}</span>
                      <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isMobileDropdownOpen && (
                      <div className="space-y-1 pl-4">
                        {item.dropdown.map((subItem) => (
                          <NavLink
                            key={subItem.label}
                            to={subItem.href}
                            className={({ isActive }) =>
                              `block rounded-lg pl-6 pr-4 py-2 text-sm transition-colors hover:bg-slate-100 ${
                                isActive ? 'text-brand-accent bg-slate-50 font-medium' : 'text-slate-500'
                              }`
                            }
                          >
                            {subItem.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-2.5 text-base font-medium transition-colors hover:bg-slate-100 ${
                      isActive ? 'text-brand-accent bg-slate-50' : 'text-slate-600'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            <div className="my-4 border-t border-slate-200 pt-4" />

            <div className="space-y-3 px-4">
              <div className="text-sm text-slate-500">
                <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1">E-mail</span>
                <a href="mailto:info@jc-svejseteknik.dk" className="hover:text-brand-accent transition-colors whitespace-nowrap">
                  info@jc-svejseteknik.dk
                </a>
              </div>
              <Link
                to="/kontakt"
                className="block w-full rounded-md bg-brand-accent py-3 text-center text-sm font-semibold tracking-wider text-white transition-colors hover:bg-brand-accent/80"
              >
                Få et tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
